import { Vec3 } from '@/core/math/Vec3';
import { ShipDef } from './ShipDef';

export interface MassProperties {
  /** Total current mass, kg. */
  total: number;
  /** Mass with all tanks empty and no cargo, kg. */
  dry: number;
  /** Propellant mass currently aboard, kg. */
  fuel: number;
  /** Cargo mass currently aboard, kg. */
  cargo: number;
  /** Centre of mass in body coordinates, metres. */
  centreOfMass: Vec3;
  /** Principal moments of inertia about the centre of mass, kg m^2. */
  inertia: Vec3;
}

interface PointMass {
  mass: number;
  x: number;
  y: number;
  z: number;
}

/**
 * Computes a vessel's mass properties from its current propellant and cargo
 * loading.
 *
 * This is recomputed every physics step, not cached at design time, because the
 * whole point is that it *changes*: a Kestrel with full tanks has its centre of
 * mass 40 cm further aft than an empty one, which shifts the moment produced by
 * the landing thrusters and changes how it handles on touchdown. Off-centre
 * cargo does the same thing more dramatically.
 *
 * The inertia model treats the structure as a solid box and everything else as
 * point masses combined through the parallel axis theorem. That is an
 * approximation, but a consistent one, and it captures the behaviour that
 * matters: heavy things far from the axis are hard to rotate.
 */
export class MassModel {
  private readonly points: PointMass[] = [];

  constructor(private readonly def: ShipDef) {}

  compute(
    fuelByTank: ReadonlyMap<string, number>,
    cargoMass: number,
    cargoOffset: Vec3 = new Vec3(0, 0, 0),
    out: MassProperties = {
      total: 0,
      dry: 0,
      fuel: 0,
      cargo: 0,
      centreOfMass: new Vec3(),
      inertia: new Vec3(),
    },
  ): MassProperties {
    const def = this.def;
    this.points.length = 0;

    let dry = def.structureMass;
    let fuel = 0;

    // Structure sits at the body origin.
    this.points.push({ mass: def.structureMass, x: 0, y: 0, z: 0 });

    for (const e of def.engines) {
      dry += e.mass;
      this.points.push({ mass: e.mass, x: e.position[0], y: e.position[1], z: e.position[2] });
    }

    for (const t of def.tanks) {
      const load = Math.max(0, fuelByTank.get(t.id) ?? 0);
      fuel += load;
      dry += t.dryMass;
      this.points.push({ mass: t.dryMass + load, x: t.position[0], y: t.position[1], z: t.position[2] });
    }

    if (def.fieldDrive) {
      dry += def.fieldDrive.mass;
      this.points.push({ mass: def.fieldDrive.mass, x: 0, y: -0.4, z: 0 });
    }
    for (const p of def.powerPlants) {
      dry += p.mass;
      this.points.push({ mass: p.mass, x: 0, y: 0.6, z: -def.size[2] * 0.18 });
    }
    for (const b of def.batteries) {
      dry += b.mass;
      this.points.push({ mass: b.mass, x: 0, y: -0.8, z: 0 });
    }
    for (const r of def.radiators) {
      dry += r.mass;
      this.points.push({ mass: r.mass, x: 0, y: 1.2, z: -def.size[2] * 0.1 });
    }

    const cargo = Math.max(0, cargoMass);
    if (cargo > 0) {
      this.points.push({ mass: cargo, x: cargoOffset.x, y: cargoOffset.y, z: cargoOffset.z });
    }

    // Centre of mass.
    let total = 0;
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (const p of this.points) {
      total += p.mass;
      cx += p.mass * p.x;
      cy += p.mass * p.y;
      cz += p.mass * p.z;
    }
    if (total <= 0) total = 1;
    cx /= total;
    cy /= total;
    cz /= total;

    // Inertia: box term for the structure, point-mass terms for everything else.
    const [sx, sy, sz] = def.size;
    const boxK = def.structureMass / 12;
    let ixx = boxK * (sy * sy + sz * sz);
    let iyy = boxK * (sx * sx + sz * sz);
    let izz = boxK * (sx * sx + sy * sy);

    for (const p of this.points) {
      const dx = p.x - cx;
      const dy = p.y - cy;
      const dz = p.z - cz;
      if (p.mass === def.structureMass && dx === -cx && dy === -cy && dz === -cz) {
        // Structure box already counted; only add its parallel-axis offset.
        ixx += p.mass * (dy * dy + dz * dz);
        iyy += p.mass * (dx * dx + dz * dz);
        izz += p.mass * (dx * dx + dy * dy);
        continue;
      }
      ixx += p.mass * (dy * dy + dz * dz);
      iyy += p.mass * (dx * dx + dz * dz);
      izz += p.mass * (dx * dx + dy * dy);
    }

    // Guard against a degenerate inertia if someone authors a zero-size hull.
    const floor = total * 0.05;
    out.total = total;
    out.dry = dry;
    out.fuel = fuel;
    out.cargo = cargo;
    out.centreOfMass.set(cx, cy, cz);
    out.inertia.set(Math.max(floor, ixx), Math.max(floor, iyy), Math.max(floor, izz));
    return out;
  }
}
